import { UserService } from './../../../core/services/user.service';
import { FileObject } from './../../../core/interfaces/file-object';
import { ChangeDetectorRef, Component, TemplateRef, ViewChild } from '@angular/core';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import * as moment from 'moment';
import { forkJoin, takeUntil, Observable } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ConsentFormsService } from 'src/app/core/services/consent-forms.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-consent-form-settings',
  templateUrl: './consent-form-settings.component.html',
  styleUrls: ['./consent-form-settings.component.scss']
})
export class ConsentFormSettingsComponent extends BaseComponent {

  @ViewChild('addConsentFormDialog') addConsentFormDialog?: TemplateRef<any>;
  @ViewChild('importConsentFormsDialog') importConsentFormsDialog?: TemplateRef<any>;
  @ViewChild('importTemplatesDialog') importTemplatesDialog?: TemplateRef<any>;
  defaultConsentTypes: any = null;
  customConsentTypes: any = null;
  editingConsentForm: any = {};

  isSupportUser: boolean = false;
  importConsentFormsJson: string = '';
  consentTemplates: any[] = [];
  selectedTemplateIds: string[] = [];


  readonly separatorKeysCodes: number[] = [13, 188]; // Enter and comma key codes
  addOnBlur = true;

  constructor(private consentFormsService: ConsentFormsService, private cd: ChangeDetectorRef, private userService: UserService, fs: FeedbackService, md: MatDialog) {
    super(fs, md);
    this.consentFormsService.consentForms$.subscribe(consentForms => {
      if (consentForms) {
        this.defaultConsentTypes = consentForms
          .filter((f: any) => !f.canDelete || f.sendWithInvite)
          .map((c: any) => { return { ...c, preview: c.consentType === 'interactive' ? this.buildInteractiveConsent(c) : c.text } });

        this.customConsentTypes = consentForms
          .filter((f: any) => f.canDelete && !f.sendWithInvite)
          .map((c: any) => { return { ...c, preview: c.consentType === 'interactive' ? this.buildInteractiveConsent(c) : c.text } });
      }
    });

    this.consentFormsService.consentTemplates$.subscribe(templates => {
      this.consentTemplates = templates || [];
    });

    this.userService.userProfile$.subscribe(user => {
      if (user && (user.id === '511f39e1-ff14-4a6d-b35a-6fcc22923b50' || user.id === '8f78c3c3-032e-4d2d-8ab3-d4209bb0a305')) {
        this.isSupportUser = true;
      } else {
        this.isSupportUser = false;
      }
    });
  }

  openImportConsentFormsDialog(event?: MouseEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.dialog.open(this.importConsentFormsDialog!, { width: '600px' });
  }

  openImportTemplatesDialog(event?: MouseEvent | null) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedTemplateIds = [];
    this.dialog.open(this.importTemplatesDialog!, { width: '600px' });
  }

  onTemplateSelectionChange(event: any) {
    this.selectedTemplateIds = event.source.selectedOptions.selected.map((opt: any) => opt.value);
  }

  async importSelectedTemplates() {
    if (this.selectedTemplateIds.length === 0) {
      this.showToastMessage('Please select at least one template to import');
      return;
    }

    const selectedTemplates = this.consentTemplates.filter(t => this.selectedTemplateIds.includes(t.id));
    
    // Check for existing consent forms with the same label
    const allConsentForms = [...(this.defaultConsentTypes || []), ...(this.customConsentTypes || [])];
    const duplicateLabels: string[] = [];
    
    for (const template of selectedTemplates) {
      const existingForm = allConsentForms.find((form: any) => 
        form.label && form.label.toLowerCase() === template.label.toLowerCase()
      );
      if (existingForm) {
        duplicateLabels.push(template.label);
      }
    }

    if (duplicateLabels.length > 0) {
      const duplicateMessage = duplicateLabels.length === 1
        ? `A consent form with the name "${duplicateLabels[0]}" already exists.`
        : `Consent forms with the following names already exist: ${duplicateLabels.join(', ')}.`;
      
      const confirmed = await this.showConfirmationDialog(
        undefined,
        `${duplicateMessage} Do you want to import anyway?`,
        [],
        'Cancel',
        'Import Anyway',
        false
      );
      
      if (!confirmed) {
        return;
      }
    }

    const importRequests: Observable<any>[] = [];

    for (const template of selectedTemplates) {
      // Clone template data and prepare for import
      const clonedTemplate: any = {
        label: template.label,
        consentType: template.consentType,
        text: template.text || '',
        procedureTitle: template.procedureTitle || '',
        condition: template.condition || '',
        treatment: template.treatment || '',
        generalRisks: template.generalRisks || '',
        specificRisks: template.specificRisks || '',
        conditionalConsent: Array.isArray(template.conditionalConsent) ? [...template.conditionalConsent] : [],
        confirmSide: template.confirmSide || false,
        sideOptions: Array.isArray(template.sideOptions) ? [...template.sideOptions] : ['Left', 'Right', 'N/A'],
        confirmImplant: template.confirmImplant || false,
        implantOptions: Array.isArray(template.implantOptions) ? [...template.implantOptions] : [],
        confirmTransfusion: template.confirmTransfusion || false,
        transfusionOptions: Array.isArray(template.transfusionOptions) ? [...template.transfusionOptions] : ['YES', 'NO'],
        additionalRequiredConsent: Array.isArray(template.additionalRequiredConsent) ? [...template.additionalRequiredConsent] : [],
        sendWithInvite: false,
        canDelete: true,
        informationSheets: []
      };

      // Create FormData with 'data' key, matching the expected format
      const formData = new FormData();
      formData.append('data', JSON.stringify(clonedTemplate));
      importRequests.push(this.consentFormsService.updateConsentForms(formData));
    }

    if (importRequests.length === 0) {
      this.showToastMessage('No templates selected');
      return;
    }

    const sub = forkJoin(importRequests)
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        // The consent forms list will be automatically updated via the consentForms$ subscription
        this.showToastMessage(`Successfully imported ${importRequests.length} consent template(s)`);
        this.dialog.closeAll();
        this.selectedTemplateIds = [];
        sub.unsubscribe();
      }, error => {
        console.error('Error importing templates:', error);
        this.showToastMessage('Error importing templates. Please try again.');
        sub.unsubscribe();
      });
  }

  openAddConsentFormDialog(event?: MouseEvent | null, sendWithInvite?: boolean, consentForm?: any) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    let formDetails;

    if (consentForm) {
      formDetails = { ...consentForm, informationSheets: consentForm.informationSheets || [], conditionalConsent: consentForm.conditionalConsent || [], additionalRequiredConsent: consentForm.additionalRequiredConsent || [] };
    } else {
      formDetails = { id: this.customConsentTypes.length + 1, sendWithInvite, consentType: sendWithInvite ? 'standard' : '', canDelete: true, text: '', conditionalConsent: [], informationSheets: [], confirmSide: false, confirmImplant: false, sideOptions: ['Left', 'Right', 'N/A'], implantOptions: [], additionalRequiredConsent: [], confirmTransfusion: false, transfusionOptions: ['YES', 'NO'] };
    }

    this.editingConsentForm = formDetails;
    this.dialog.open(this.addConsentFormDialog!, { width: '600px' });
  }

  setSelectedConsentType(event: MatSelectChange) {
    if (typeof this.editingConsentForm.id === 'number') {
      if (event.value === 'interactive') {
        this.fillDefaultInteractiveDetails(this.editingConsentForm);
      } else {
        this.clearDefaultInteractiveDetails(this.editingConsentForm);
      }
    }
  }

  fillDefaultInteractiveDetails(consent: any) {
    this.editingConsentForm = {
      ...this.editingConsentForm,
      condition: `CONDITION\n- The doctor has explained that you have the following condition:\n`,
      treatment: `TREATMENT\n- The condition requires the following procedure:\n${consent.procedureTitle ? consent.procedureTitle + '\n' : ''}`,
      generalRisks: `RISKS ASSOCIATED WITH THE PROCEDURE\nWhile every effort will be made to make your procedure as safe as possible, there are potential risks and complications with this procedure. They include but are not limited to the following:\n`,
      specificRisks: `HIGHLIGHTED RISKS ASSOCIATED WITH THE PROCEDURE\n`,
      text: `PATIENT CONSENT\nI acknowledge that the doctor has explained:\n• my medical condition and the proposed procedure, including additional treatment if the doctor finds something unexpected. I understand the risks, including the risks that are specific to me.\n•	the anaesthetic required for this procedure. I understand the risks, including the risks that are specific to me.\n•	other relevant procedure/treatment options and their associated risks.\n•	my prognosis and the risks of not having the procedure.\n•	that no guarantee has been made that the procedure will improve my condition even though it has been carried out with due professional care.\n•	the procedure may include a blood transfusion.\n•	tissues and blood may be removed and could be used for diagnosis ors management of my condition, stored and disposed of sensitively by the hospital.\n•	if immediate life-threatening events happen during the procedure, they will be treated based on my discussions with the doctor or my Acute Resuscitation Plan.\n•	I was able to ask questions and raise concerns with the doctor about my condition, the proposed procedure and its risks, and my treatment options. My questions and concerns have been discussed and answered to my satisfaction.\n•	I understand I have the right to change my mind at any time, including after I have signed this form but, preferably following a discussion with my doctor.\n•	I understand that image/s or video footage may be recorded as part of and during my procedure and that these image/s or video/s will assist the doctor to provide appropriate treatment.`,
    }
  }

  clearDefaultInteractiveDetails(consent: any) {
    this.editingConsentForm = {
      ...this.editingConsentForm,
      condition: ``,
      treatment: ``,
      generalRisks: ``,
      specificRisks: ``,
      text: ``,
    }
  }

  onConfirmSideChange(event: MatSlideToggleChange) {
    if (event.checked && (!this.editingConsentForm.sideOptions || this.editingConsentForm.sideOptions.length === 0)) {
      this.editingConsentForm.sideOptions = ['Left', 'Right', 'N/A'];
    }
  }

  onConfirmImplantChange(event: MatSlideToggleChange) {
    if (event.checked && (!this.editingConsentForm.implantOptions || this.editingConsentForm.implantOptions.length === 0)) {
      this.editingConsentForm.implantOptions = [];
    }
  }

  onConfirmTransfusionChange(event: MatSlideToggleChange) {
    if (event.checked && (!this.editingConsentForm.transfusionOptions || this.editingConsentForm.transfusionOptions.length === 0)) {
      this.editingConsentForm.transfusionOptions = ['YES', 'NO'];
    }
  }

  removeConditionalConsent(item: any) {
    const index = this.editingConsentForm.conditionalConsent.indexOf(item);

    if (index >= 0) {
      this.editingConsentForm.conditionalConsent.splice(index, 1);
    }
    this.cd.detectChanges();
  }

  removeRequiredConsent(item: any) {
    const index = this.editingConsentForm.additionalRequiredConsent.indexOf(item);

    if (index >= 0) {
      this.editingConsentForm.additionalRequiredConsent.splice(index, 1);
    }
    this.cd.detectChanges();
  }

  removeProcedureSideOption(item: any) {
    const index = this.editingConsentForm.sideOptions.indexOf(item);

    if (index >= 0) {
      this.editingConsentForm.sideOptions.splice(index, 1);
    }
    this.cd.detectChanges();
  }

  removeImplantOption(item: any) {
    const index = this.editingConsentForm.implantOptions.indexOf(item);

    if (index >= 0) {
      this.editingConsentForm.implantOptions.splice(index, 1);
    }
    this.cd.detectChanges();
  }

  editConditionalConsent(item: string, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.removeConditionalConsent(item);
      return;
    }

    const index = this.editingConsentForm.conditionalConsent.indexOf(item);
    if (index >= 0) {
      this.editingConsentForm.conditionalConsent[index] = value;
    }

    this.cd.detectChanges();

  }

  editRequiredConsent(item: string, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.removeRequiredConsent(item);
      return;
    }

    const index = this.editingConsentForm.additionalRequiredConsent.indexOf(item);
    if (index >= 0) {
      this.editingConsentForm.additionalRequiredConsent[index] = value;
    }

    this.cd.detectChanges();

  }

  editProcedureSideOption(item: string, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.removeProcedureSideOption(item);
      return;
    }

    const index = this.editingConsentForm.sideOptions.indexOf(item);
    if (index >= 0) {
      this.editingConsentForm.sideOptions[index] = value;
    }

    this.cd.detectChanges();

  }

  editImplantOption(item: string, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.removeImplantOption(item);
      return;
    }

    const index = this.editingConsentForm.implantOptions.indexOf(item);
    if (index >= 0) {
      this.editingConsentForm.implantOptions[index] = value;
    }

    this.cd.detectChanges();

  }

  addConditionalConsent(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.editingConsentForm.conditionalConsent.push(value);
    }
    event.chipInput.clear();
    this.cd.detectChanges();
  }


  addRequiredConsent(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.editingConsentForm.additionalRequiredConsent.push(value);
    }
    event.chipInput.clear();
    this.cd.detectChanges();
  }

  addProcedureSideOption(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.editingConsentForm.sideOptions.push(value);
    }
    event.chipInput.clear();
    this.cd.detectChanges();
  }

  addImplantOption(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.editingConsentForm.implantOptions.push(value);
    }
    event.chipInput.clear();
    this.cd.detectChanges();
  }

  saveConsentForm(consentForm: any) {
    if (typeof consentForm.id !== 'string') {
      delete consentForm.id;
    }

    if (consentForm.confirmSide && (!consentForm.sideOptions || consentForm.sideOptions.length === 0)) {
      this.showToastMessage('Side options are required when enabling the "Patient To Confirm Procedure Side" option. Please add at least one procedure side option to continue.');
      return;
    }


    if (consentForm.confirmImplant && (!consentForm.implantOptions || consentForm.implantOptions.length === 0)) {
      this.showToastMessage('Implant options are required when enabling the "Patient To Confirm Implant" option. Please add at least one implant option to continue.');
      return;
    }

    const newInformationSheets = consentForm.informationSheets?.filter((obj: any) => !obj.id) as any[] || [];

    const formData = new FormData();
    formData.append('data', JSON.stringify(consentForm));

    const mediaAppendPromises = newInformationSheets.map((obj, index) => {
      return fetch(obj.url)
        .then(response => response.blob())
        .then(blob => {
          formData.append(obj.name, blob, `${moment().unix()}.${obj.type.split('/')[1]}`);
        });
    });

    Promise.all(mediaAppendPromises)
      .then(() => {
        const sub = this.consentFormsService.updateConsentForms(formData)
          .pipe(takeUntil(this.destroy$))
          .subscribe(
            (response) => {
              this.dialog.closeAll();
              this.showToastMessage('Consent forms updated successfully');
              sub.unsubscribe();
            },
            (error) => {
              console.error('Error updating consent forms:', error);
              this.showToastMessage('There was an error updating consent forms. Please try again.');
              sub.unsubscribe();
            });
      });
  }

  validateJson() {
    try {
      console.log(this.importConsentFormsJson);
      JSON.parse(this.importConsentFormsJson);
    } catch (error) {
      this.showToastMessage('Invalid JSON. Please check your JSON and try again.');
    }
  }

  async importConsentForms() {
    try {
      const parsed = JSON.parse(this.importConsentFormsJson);

      // Normalize to array: if it's a single object, wrap it in an array
      const json = Array.isArray(parsed) ? parsed : [parsed];

      if (json.length === 0) {
        this.showToastMessage('No consent forms to import');
        return;
      }

      const validConsentForms = json
        .filter((item: any) =>
          item.consentType &&
          item.consentType === 'interactive' &&
          item.label &&
          item.text &&
          item.condition &&
          item.treatment &&
          (item.generalRisks || Array.isArray(item.generalRisks)) &&
          (item.specificRisks || Array.isArray(item.specificRisks))
        );

      if (validConsentForms.length === 0) {
        this.showToastMessage('No valid consent forms to import');
        return;
      }

      this.showToastMessage('Importing consent forms...');

      const importRequests = [];
      for (let item of validConsentForms) {
        delete item.id;
        // Convert arrays to strings for generalRisks and specificRisks if they are arrays
        if (Array.isArray(item.generalRisks)) {
          item.generalRisks = item.generalRisks.join('\n');
        }
        if (Array.isArray(item.specificRisks)) {
          item.specificRisks = item.specificRisks.join('\n');
        }

        // Ensure headings are present for interactive consent forms
        if (item.consentType === 'interactive') {
          // Ensure CONDITION heading is present

          item.condition = `CONDITION\n- The doctor has explained that you have the following condition:\n`;
          item.treatment = `TREATMENT\n- The condition requires the following procedure:\n`;
          item.specificRisks = `HIGHLIGHTED RISKS ASSOCIATED WITH THE PROCEDURE\n`;


          // Ensure RISKS ASSOCIATED WITH THE PROCEDURE heading is present
          if (item.generalRisks && !item.generalRisks.trim().toUpperCase().includes('RISKS ASSOCIATED WITH THE PROCEDURE')) {
            item.generalRisks = `RISKS ASSOCIATED WITH THE PROCEDURE\nWhile every effort will be made to make your procedure as safe as possible, there are potential risks and complications with this procedure. They include but are not limited to the following:\n${item.generalRisks}`;
          } else if (!item.generalRisks) {
            item.generalRisks = `RISKS ASSOCIATED WITH THE PROCEDURE\nWhile every effort will be made to make your procedure as safe as possible, there are potential risks and complications with this procedure. They include but are not limited to the following:\n`;
          }

        }

        // Set defaults only if not already present
        if (!item.additionalRequiredConsent) {
          item.additionalRequiredConsent = [];
        }
        if (!item.conditionalConsent) {
          item.conditionalConsent = [];
        }
        if (!item.sideOptions) {
          item.sideOptions = ['Left', 'Right', 'N/A'];
        }
        if (!item.implantOptions) {
          item.implantOptions = [];
        }
        if (!item.transfusionOptions) {
          item.transfusionOptions = ['YES', 'NO'];
        }
        if (item.confirmSide === undefined) {
          item.confirmSide = false;
        }
        if (item.confirmImplant === undefined) {
          item.confirmImplant = false;
        }
        if (item.confirmTransfusion === undefined) {
          item.confirmTransfusion = false;
        }

        item.sendWithInvite = false;
        item.procedureTitle = '';
        item.canDelete = true;
        // check for existing consent form with the same label
        const existingConsentForm = this.customConsentTypes.find((cb: any) => cb.label === item.label);
        if (existingConsentForm) {
          const confirmAdd = await this.showConfirmationDialog(undefined, 'Consent form with the same title already exists. Do you want to add it anyway?', [], 'Ignore', 'Add Anyway', false);
          if (confirmAdd) {
            // Create FormData with 'data' key, matching the expected format
            const formData = new FormData();
            formData.append('data', JSON.stringify(item));
            importRequests.push(this.consentFormsService.updateConsentForms(formData));
          } else {
            continue;
          }
        } else {
          // Create FormData with 'data' key, matching the expected format
          const formData = new FormData();
          formData.append('data', JSON.stringify(item));
          importRequests.push(this.consentFormsService.updateConsentForms(formData));
        }
      }

      if (importRequests.length === 0) {
        this.showToastMessage('No valid consent forms to import');
        return;
      }
      const sub = forkJoin(importRequests)
        .pipe(takeUntil(this.destroy$))
        .subscribe(results => {
          // The consent forms list will be automatically updated via the consentForms$ subscription
          this.showToastMessage('Consent forms imported successfully');
          this.dialog.closeAll();
          sub.unsubscribe();
        });

    } catch (error) {
      // failed to parse JSON
      this.showToastMessage('Failed to parse JSON. Please check your JSON and try again.');
    }
  }

  buildInteractiveConsent(consent: any): string {
    return this.consentFormsService.buildInteractiveConsentPreview(consent);
  }

  fullScreenConsentForm: any = null;
  @ViewChild('fullScreenDialog') fullScreenDialog?: TemplateRef<any>;
  viewConsentFullscreen(form: any) {
    this.fullScreenConsentForm = { ...form, consentLabel: form.name, consentText: this.buildInteractiveConsent(form) };
    this.dialog.open(this.fullScreenDialog!);
  }

  editInformationSheetName(sheet: any, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.removeInformationSheet(sheet);
      return;
    }

    const index = this.editingConsentForm.informationSheets.indexOf(sheet);
    if (index >= 0) {
      this.editingConsentForm.informationSheets[index].name = value;
    }

    const current = this.editingConsentForm.informationSheets[index];
    if (current.id) {
      this.saveInformationSheetChanges({ id: current.id, name: value })

    }
  }


  removeInformationSheet(sheet: any) {
    const index = this.editingConsentForm.informationSheets.indexOf(sheet);

    if (index >= 0) {
      this.editingConsentForm.informationSheets.splice(index, 1);
    }
  }

  private saveInformationSheetChanges(changes: any) {
    const sub = this.consentFormsService.updateInformationSheet(changes)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => { sub.unsubscribe(); },
        (error) => {
          console.error('Error updating the information sheet:', error);
          this.showToastMessage('There was an error updating the information sheet. Please try again.');
          sub.unsubscribe();
        }

      )
  }


  private getFileName(file: any) {
    const raw = file?.name;
    if (!raw || typeof raw !== 'string') {
      return 'Information sheet';
    }
    const parts = raw.split('.');
    if (parts.length <= 1) {
      return raw;
    }
    return parts.slice(0, -1).join('.');
  }


  onInformationSheetUpload(files: FileObject[]) {

    const newMediaObjects = files.map(file => ({
      ...file,
      //url: URL.createObjectURL(file),
      type: file.type,
      name: this.getFileName(file),
    }));

    // Check for duplicates and avoid appending unnecessarily
    const uniqueMediaObjects = newMediaObjects.filter(newObj =>
      !this.editingConsentForm.informationSheets.some((existingObj: any) => existingObj.url === newObj.url)
    );

    this.editingConsentForm.informationSheets = [...this.editingConsentForm.informationSheets, ...uniqueMediaObjects];
  }

  async deleteConsentForm(id: string | number) {
    const confirmed = await this.showConfirmationDialog();

    if (typeof id === 'number') {
      this.customConsentTypes = this.customConsentTypes.filter((cb: any) => cb.id !== id);
      return;
    }

    if (confirmed) {
      const sub = this.consentFormsService.deleteConsentForm(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (response: any) => {
            this.showToastMessage('Consent form deleted successfully');
            sub.unsubscribe();
          },
          (error) => {
            console.error('Error deleting consent form:', error);
            this.showToastMessage('There was an error deleting the consent form. Please try again.');
            sub.unsubscribe();
          }
        );
    }
  }

}
